import { Access } from "../models/access";
import { Job } from "../models/job";
import { Location } from "../models/location";
import { CustomerLocations, RoomJob } from "../models/sales";
import { JobSubStatus, JobStatus as OrderStatus } from "./enums";

interface IRemoveAddressProps {
  from: string;
  locationIndex: number;
  customerLocation: CustomerLocations;
  modalReference: any;
  inventory: RoomJob[];
  updateAddress: (locations: CustomerLocations) => void;
}

export const onRemoveAddress = ({
  from,
  locationIndex,
  customerLocation,
  modalReference,
  inventory,
  updateAddress,
}: IRemoveAddressProps) => {
  const customerLocationToUpdate: CustomerLocations = customerLocation;

  if (from === "delivery") {
    let hasItemWithThisLocation = false;
    let hasMaterialWithThisLocation = false;
    const location = customerLocationToUpdate.locations
      ? customerLocationToUpdate.locations[locationIndex]
      : null;

    if (location) {
      inventory.forEach((roomJob) => {
        if (roomJob?.itemsJob?.length > 0) {
          hasItemWithThisLocation =
            roomJob.itemsJob.filter((item) => item.itemJob.destinationResidenceId === location.id)
              .length > 0;
        }

        if (roomJob?.materials?.length > 0) {
          hasMaterialWithThisLocation =
            roomJob.materials.filter((material) => material.destinationResidenceId === location.id)
              .length > 0;
        }
      });

      if (hasItemWithThisLocation || hasMaterialWithThisLocation) {
        modalReference?.current?.openModal(location.id);
      } else if (customerLocationToUpdate.locations) {
        if (!Number.isNaN(Number(location.id)))
          customerLocationToUpdate.locations[locationIndex].id = `${location.id}delete`;
        else customerLocationToUpdate.locations.splice(locationIndex, 1);
      }
    }
  } else {
    const location = customerLocationToUpdate.locations
      ? customerLocationToUpdate.locations[locationIndex]
      : null;

    if (location) {
      if (customerLocationToUpdate.locations) {
        if (!Number.isNaN(Number(location.id)))
          customerLocationToUpdate.locations[locationIndex].id = `${location.id}delete`;
        else customerLocationToUpdate.locations.splice(locationIndex, 1);
      }
    }
  }
  updateAddress(customerLocationToUpdate);
};

interface IConfirmRemoveAddress {
  addressId: string | number;
  customerDestinationLocation: CustomerLocations;
  inventory: RoomJob[];
  updateAddress: (inventory: RoomJob[], customerLocation: CustomerLocations) => void;
}

export const onConfirmRemoveAddress = ({
  addressId,
  customerDestinationLocation,
  inventory,
  updateAddress,
}: IConfirmRemoveAddress) => {
  const customerLocationToUpdate: CustomerLocations = customerDestinationLocation;
  const addressIndex = customerLocationToUpdate
    ? customerLocationToUpdate.locations?.findIndex((item) => item.id === addressId)
    : -1;

  if (addressId && inventory) {
    const inventoryWithAddress: boolean[] = [];
    const materialWithAddress: boolean[] = [];

    inventory.forEach((roomJob) => {
      if (roomJob.itemsJob && roomJob.itemsJob.length > 0) {
        roomJob.itemsJob.forEach((itemJob) => {
          if (
            itemJob?.itemJob?.destinationResidenceId &&
            itemJob?.itemJob?.destinationResidenceId === addressId
          ) {
            inventoryWithAddress.push(true);
          }
        });
      }
      if (roomJob.materials && roomJob.materials.length > 0) {
        roomJob.materials.forEach((material) => {
          if (material.destinationResidenceId && material.destinationResidenceId === addressId) {
            materialWithAddress.push(true);
          }
        });
      }
    });

    if (
      customerLocationToUpdate.locations &&
      customerLocationToUpdate.locations[0] &&
      customerLocationToUpdate.locations[0].id &&
      addressIndex &&
      addressIndex > -1
    ) {
      if (!Number.isNaN(Number(addressId)))
        customerLocationToUpdate.locations[addressIndex].id = `${addressId}delete`;
      else customerLocationToUpdate.locations.splice(addressIndex, 1);

      inventory.forEach((roomJob) => {
        if (inventoryWithAddress.length > 0) {
          if (roomJob.itemsJob && roomJob.itemsJob.length > 0) {
            roomJob.itemsJob.forEach((itemJob) => {
              if (
                itemJob.itemJob.destinationResidenceId === addressId &&
                customerLocationToUpdate.locations
              ) {
                itemJob.itemJob = {
                  ...itemJob.itemJob,
                  destinationResidenceId: customerLocationToUpdate?.locations[0].id,
                  destinationResidence: customerLocationToUpdate?.locations[0] as Location,
                };
              }
            });
          }
        }

        if (materialWithAddress.length > 0) {
          if (roomJob.materials && roomJob.materials.length > 0) {
            const { materials } = roomJob;

            roomJob.materials.forEach((material, index: number) => {
              if (
                material.destinationResidenceId === addressId &&
                customerLocationToUpdate.locations
              ) {
                materials[index] = {
                  ...material,
                  destinationResidenceId: customerLocationToUpdate.locations[0].id,
                  destinationResidence: customerLocationToUpdate.locations[0] as Location,
                };
              }
            });
            roomJob.materials = [...materials];
          }
        }
      });

      updateAddress(inventory, customerLocationToUpdate);
    }
  }
};

type StatusToCheckActions = JobSubStatus | string;
export const isSubStatusAvailableToActionsQuote = (
  statusToCheck: StatusToCheckActions | undefined,
  isEditingIcon?: boolean,
  isVideoSurveyButton?: boolean
) => {
  if (!statusToCheck) return false;

  let unavailableStatuses: StatusToCheckActions[] = [];

  if (isEditingIcon) {
    unavailableStatuses = [JobSubStatus.WON.toLowerCase()] as StatusToCheckActions[];
  } else if (isVideoSurveyButton) {
    unavailableStatuses = [
      JobSubStatus.LOST.toLowerCase(),
      JobSubStatus.CANCELLED.toLowerCase(),
    ] as StatusToCheckActions[];
  } else {
    unavailableStatuses = [
      JobSubStatus.WON.toLowerCase(),
      JobSubStatus.LOST.toLowerCase(),
      JobSubStatus.CANCELLED.toLowerCase(),
    ] as StatusToCheckActions[];
  }

  return !unavailableStatuses.includes(statusToCheck.toLowerCase());
};

export const isJobQuote = (job?: Job) => {
  if (!job) return false;

  return job.wasQuote;
};

export const isOrderNeitherDeliveredNorCancelled = (orderStatus?: string) => {
  if (!orderStatus) return false;

  return orderStatus !== OrderStatus.DELIVERED && orderStatus !== OrderStatus.CANCELLED;
};

export const haveChangesAccess = (access?: Access) => {
  let haveChange = false;

  if (!access) return haveChange;

  const keys = Object.keys(access) as Array<keyof typeof access>;

  for (const key of keys) {
    const value = access[key];

    if (key === "instructions") continue;

    if (value instanceof Boolean && value) {
      haveChange = true;
      continue;
    }
    if (key === "streetsAccesses") {
      haveChange = access[key].some((row) => row.name);
      continue;
    }
    if (value) {
      haveChange = true;
    }
  }

  return haveChange;
};
